/**
 * App Update Service
 *
 * Handles OTA web bundle updates (both platforms) and APK native updates (Android only).
 * Uses @capgo/capacitor-updater for web bundle OTA in manual mode.
 * Uses @capawesome-team/capacitor-file-opener for triggering APK installs.
 */

import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { FileOpener } from '@capawesome-team/capacitor-file-opener'
import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { App } from '@capacitor/app'
import { apiClient } from '@/services/api/api-client'
import { APP_WEB_VERSION } from '@/utils/constants'

export interface UpdateInfo {
    webVersion: string
    nativeVersion: string
    webBundleUrl: string
    apkUrl: string
    forceUpdate: boolean
    changelog: string
}

export interface UpdateCheckResult {
    webUpdateAvailable: boolean
    nativeUpdateAvailable: boolean
    updateInfo: UpdateInfo | null
}

type UpdateListener = (result: UpdateCheckResult) => void
type DownloadProgressListener = (percent: number) => void

class AppUpdateService {
    private listeners: Set<UpdateListener> = new Set()
    private downloadProgressListeners: Set<DownloadProgressListener> = new Set()
    private isChecking = false
    private lastCheckResult: UpdateCheckResult | null = null

    /**
     * Initialize the updater plugin.
     * Must be called early in app startup to prevent rollback.
     */
    async initialize(): Promise<void> {
        if (Capacitor.getPlatform() === 'web') return

        try {
            await CapacitorUpdater.notifyAppReady()
            console.log('[AppUpdate] notifyAppReady called successfully')
        } catch (error) {
            console.error('[AppUpdate] Failed to call notifyAppReady:', error)
        }
    }

    /**
     * Check for available updates by calling the server version endpoint.
     */
    async checkForUpdates(): Promise<UpdateCheckResult> {
        if (this.isChecking) {
            return this.lastCheckResult ?? { webUpdateAvailable: false, nativeUpdateAvailable: false, updateInfo: null }
        }

        if (Capacitor.getPlatform() === 'web') {
            return { webUpdateAvailable: false, nativeUpdateAvailable: false, updateInfo: null }
        }

        this.isChecking = true

        try {
            const updateInfo = await apiClient.get<UpdateInfo>('/api/warden/version')

            const webUpdateAvailable = this.isNewerVersion(updateInfo.webVersion, APP_WEB_VERSION)
            const nativeUpdateAvailable =
                Capacitor.getPlatform() === 'android' && await this.isNativeUpdateAvailable(updateInfo.nativeVersion)

            const result: UpdateCheckResult = {
                webUpdateAvailable,
                nativeUpdateAvailable,
                updateInfo
            }

            this.lastCheckResult = result
            this.notifyListeners(result)

            console.log('[AppUpdate] Check result:', {
                webUpdateAvailable,
                nativeUpdateAvailable,
                currentWebVersion: APP_WEB_VERSION,
                serverWebVersion: updateInfo.webVersion,
                serverNativeVersion: updateInfo.nativeVersion
            })

            return result
        } catch (error) {
            console.error('[AppUpdate] Failed to check for updates:', error)
            return { webUpdateAvailable: false, nativeUpdateAvailable: false, updateInfo: null }
        } finally {
            this.isChecking = false
        }
    }

    /**
     * Download and queue a web bundle OTA update for next app start.
     * Does NOT exit the app — the caller should prompt the user to restart.
     */
    async applyWebUpdate(url: string, version: string): Promise<void> {
        console.log('[AppUpdate] Downloading web bundle:', version)

        // Listen for download progress
        const progressHandle = await CapacitorUpdater.addListener('download', (event) => {
            this.notifyDownloadProgress(event.percent)
        })

        try {
            const bundle = await CapacitorUpdater.download({ url, version })
            console.log('[AppUpdate] Bundle downloaded:', bundle.id)

            // Queue the bundle for next app start instead of applying immediately.
            // CapacitorUpdater.set() reloads the web view in-place, which breaks
            // Ionic's router lifecycle (dashboard renders empty). Using next()
            // queues it without reloading — the caller should prompt user to restart.
            await CapacitorUpdater.next({ id: bundle.id })
            console.log('[AppUpdate] Bundle queued for next start:', bundle.id)
        } catch (error) {
            console.error('[AppUpdate] Failed to apply web update:', error)
            throw error
        } finally {
            progressHandle.remove()
        }
    }

    /**
     * Exit the app for a clean cold restart.
     * On iOS, App.exitApp() is a no-op (Apple prohibits programmatic termination),
     * so we reload the WebView instead — the queued bundle from next() will be applied.
     */
    async exitApp(): Promise<void> {
        if (Capacitor.getPlatform() === 'ios') {
            window.location.reload()
        } else {
            await App.exitApp()
        }
    }

    /**
     * Download APK and trigger Android install prompt.
     * Uses native Filesystem.downloadFile() to bypass CORS and avoid WebView memory issues.
     */
    async downloadAndInstallApk(url: string): Promise<void> {
        if (Capacitor.getPlatform() !== 'android') {
            throw new Error('APK install is only available on Android')
        }

        console.log('[AppUpdate] Downloading APK from:', url)

        const progressHandle = await Filesystem.addListener('progress', (progress) => {
            if (progress.contentLength > 0) {
                this.notifyDownloadProgress(Math.round((progress.bytes / progress.contentLength) * 100))
            }
        })

        try {
            const fileName = 'warden-update.apk'
            const result = await Filesystem.downloadFile({
                url,
                path: fileName,
                directory: Directory.Cache,
                progress: true
            })

            console.log('[AppUpdate] APK downloaded to:', result.path)

            // Open APK to trigger system installer
            await FileOpener.openFile({
                path: result.path!,
                mimeType: 'application/vnd.android.package-archive'
            })
        } catch (error) {
            console.error('[AppUpdate] Failed to download/install APK:', error)
            throw error
        } finally {
            progressHandle.remove()
        }
    }

    /**
     * Compare semantic versions. Returns true if serverVersion > currentVersion.
     */
    private isNewerVersion(serverVersion: string, currentVersion: string): boolean {
        const server = serverVersion.split('.').map(Number)
        const current = currentVersion.split('.').map(Number)

        for (let i = 0; i < Math.max(server.length, current.length); i++) {
            const s = server[i] ?? 0
            const c = current[i] ?? 0
            if (s > c) return true
            if (s < c) return false
        }
        return false
    }

    /**
     * Check if the native (APK) version on server is newer than installed.
     */
    private async isNativeUpdateAvailable(serverNativeVersion: string): Promise<boolean> {
        try {
            const current = await CapacitorUpdater.current()
            const installedNativeVersion = current.native
            return this.isNewerVersion(serverNativeVersion, installedNativeVersion)
        } catch (error) {
            console.error('[AppUpdate] Failed to get current native version:', error)
            return false
        }
    }

    /**
     * Subscribe to update check results.
     */
    addUpdateListener(listener: UpdateListener): () => void {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    /**
     * Subscribe to download progress events.
     */
    addDownloadProgressListener(listener: DownloadProgressListener): () => void {
        this.downloadProgressListeners.add(listener)
        return () => this.downloadProgressListeners.delete(listener)
    }

    private notifyListeners(result: UpdateCheckResult): void {
        this.listeners.forEach(listener => {
            try { listener(result) } catch (e) { console.error('[AppUpdate] Listener error:', e) }
        })
    }

    private notifyDownloadProgress(percent: number): void {
        this.downloadProgressListeners.forEach(listener => {
            try { listener(percent) } catch (e) { console.error('[AppUpdate] Progress listener error:', e) }
        })
    }

    /**
     * Get the last check result without making a new request.
     */
    getLastCheckResult(): UpdateCheckResult | null {
        return this.lastCheckResult
    }
}

export const appUpdateService = new AppUpdateService()
export default appUpdateService
